<mat-card class="bulk-validation-card">
  <mat-card-header>
    <mat-card-title>Bulk GitHub Code Validation</mat-card-title>
    <mat-card-subtitle>
      Runs lightweight code validation across every application that exposes a GitHub repository in its Orion manifest,
      using your GitHub Enterprise PAT.
    </mat-card-subtitle>
  </mat-card-header>

  <mat-card-content>
    @if (error) {
      <div class="alert alert-danger">
        <mat-icon>error_outline</mat-icon>
        {{ error }}
      </div>
    }

    <div class="bulk-controls">
      <div class="form-group bulk-filter-field">
        <label class="form-label" for="revalidateAfterDays">Skip applications validated in the last (days)</label>
        <input
          id="revalidateAfterDays"
          type="number"
          class="form-control"
          min="0"
          max="365"
          [(ngModel)]="revalidateAfterDays"
          (change)="loadPreview()"
          [disabled]="isRunActive">
      </div>

      <div class="form-group bulk-filter-field">
        <label class="form-label" for="branchPreset">Branch to validate</label>
        <div class="branch-fields">
          <select
            id="branchPreset"
            class="form-control"
            [(ngModel)]="branchPreset"
            (ngModelChange)="onBranchPresetChange()"
            [disabled]="isRunActive">
            <option value="main">main</option>
            <option value="master">master</option>
            <option value="develop">develop</option>
            <option value="custom">Custom...</option>
          </select>

          @if (branchPreset === 'custom') {
          <input
            id="defaultBranch"
            type="text"
            class="form-control"
            placeholder="Branch name"
            [(ngModel)]="defaultBranch"
            (change)="loadPreview()"
            [disabled]="isRunActive">
          }
        </div>
      </div>

      <div class="form-group bulk-filter-field exclusion-field">
        <label class="form-label" for="excludedKeyReasons">Exclude apps using CMDB filters (optional)</label>
        <mat-select
          id="excludedKeyReasons"
          class="form-control exclusion-select"
          multiple
          panelClass="exclusion-select-panel"
          placeholder="Nothing excluded"
          [(ngModel)]="excludedKeyReasons"
          (ngModelChange)="loadPreview()"
          [disabled]="isRunActive">
          @for (reason of availableKeyReasons; track reason) {
          <mat-option [value]="reason">{{ reason }}</mat-option>
          }
        </mat-select>
        <small class="text-muted">
          Select the CMDB conditions to exclude from this bulk run. Decommission / Retire checks disposition,
          lifecycle status, assessment outcome, and key reason for explicit retirement directives.
        </small>
      </div>

      <div class="bulk-actions bulk-actions-aligned">
        <button class="btn btn-primary" (click)="onStart()" [disabled]="isRunActive || isStarting || isStatusLoading">
          @if (isStarting) {
            <app-spinner [isLoading]="true" diameter="18" loadingText="Starting..." [showText]="false"></app-spinner>
          } @else {
            Start Bulk Validation
          }
        </button>

        @if (latestRun && latestRun.status === 'Running') {
          <button class="btn btn-outline-secondary" (click)="onPause()">Pause</button>
        }
        @if (latestRun && latestRun.status === 'Paused') {
          <button class="btn btn-outline-secondary" (click)="onResume()">Resume</button>
        }        @if (isRunActive) {
          <button class="btn btn-outline-danger" (click)="onCancel()">Cancel</button>
        }
      </div>
    </div>

    @if (preview && !isRunActive) {
      <div class="alert alert-info scope-summary">
        <strong>{{ preview.eligibleRepositoryCount }}</strong> repositor(ies) across
        <strong>{{ preview.eligibleCount }}</strong> application(s) will be validated on branch
        <strong>{{ preview.defaultBranch }}</strong>
        ({{ preview.gitHubApplicationCount }} application(s) have a GitHub repository,
        {{ preview.skippedRecentlyValidatedCount }} repositor(ies) skipped as recently validated).
        @if (preview.excludedKeyReasons.length > 0) {
          <div class="mt-1">
            {{ preview.skippedExcludedCount }} repositor(ies) excluded by selected CMDB filter(s):
            {{ preview.excludedKeyReasons.join(', ') }}.
          </div>
        }
        @if (!preview.hasGitHubPat) {
          <div class="mt-1 text-danger">No GitHub PAT is configured for your account yet — you will be asked for one.</div>
        }
      </div>
    }

    @if (isStatusLoading) {
      <app-spinner></app-spinner>
    }

    @if (run) {
      @if (history.length > 1) {
        <div class="run-history-picker">
          <label for="runHistory"><strong>Run:</strong></label>
          <select id="runHistory" class="form-control run-history-select"
                  [ngModel]="selectedRunId" (ngModelChange)="onRunSelectionChange($event)">
            @for (historyRun of history; track historyRun.id) {
            <option [ngValue]="historyRun.id">{{ getRunOptionLabel(historyRun) }}</option>
            }
          </select>
        </div>
      }

      <div class="run-progress">
        <div class="run-progress-header">
          <span class="badge {{ getStatusClass(run.status) }}">{{ run.status }}</span>
          <span class="run-meta">
            Run #{{ run.id }} &middot; branch {{ run.defaultBranch }} &middot; started by {{ run.requestedByEmail || 'unknown' }}
            @if (run.startedAt) {
              &middot; {{ run.startedAt | utcDate }}
            }
          </span>
        </div>

        <mat-progress-bar mode="determinate" [value]="progressPercent"></mat-progress-bar>

        <div class="run-counters">
          <span><strong>{{ run.processedCount }}</strong> / {{ run.totalRepositories }} repositories processed</span>
          <span class="text-muted">across {{ run.totalApplications }} application(s)</span>
          <button type="button" class="counter-link text-success" (click)="showSucceededJobs()"
                  [disabled]="run.successCount === 0" title="Show succeeded jobs">
            <strong>{{ run.successCount }}</strong> succeeded
          </button>
          <button type="button" class="counter-link text-danger" (click)="showFailedJobs()"
                  [disabled]="run.failedCount === 0" title="Show failed jobs">
            <strong>{{ run.failedCount }}</strong> failed
          </button>
          <button type="button" class="counter-link text-warning" (click)="showConflictJobs()"
                  [disabled]="run.conflictCount === 0" title="Show jobs where code evidence conflicts with CMDB">
            <strong>{{ run.conflictCount }}</strong> CMDB conflicts
          </button>
          <span class="text-muted"><strong>{{ run.skippedCount }}</strong> skipped</span>
          @if (run.excludedCount > 0) {
          <span class="text-muted"><strong>{{ run.excludedCount }}</strong> excluded by selected CMDB filter(s)</span>
          }
          <span class="token-summary"
                [title]="run.totalTokens + ' total tokens: ' + run.totalPromptTokens + ' prompt, ' + run.totalCompletionTokens + ' completion'">
            <mat-icon aria-hidden="true">data_usage</mat-icon>
            <strong>{{ formatTokenCount(run.totalTokens) }}</strong> AI tokens
            <span class="text-muted">({{ formatTokenCount(run.totalPromptTokens) }} prompt / {{ formatTokenCount(run.totalCompletionTokens) }} completion)</span>
          </span>
        </div>

        @if (run.currentApplicationName && isRunActive && selectedRunId === latestRun?.id) {
          <div class="run-current text-muted">Last processed: {{ run.currentApplicationName }}</div>
        }

        @if (run.errorMessage) {
          <div class="alert alert-danger mt-2">{{ run.errorMessage }}</div>
        }
      </div>

      @if (run.processedCount > 0) {
        <div class="job-list-toolbar">
          <label for="jobsOutcomeFilter"><strong>Show jobs:</strong></label>
          <select id="jobsOutcomeFilter" class="form-control outcome-filter"
                  [(ngModel)]="jobsOutcomeFilter" (ngModelChange)="onJobsOutcomeFilterChange()">
            <option value="all">All jobs</option>
            <option value="succeeded">Succeeded only ({{ run.successCount }})</option>
            <option value="failed">Failed only ({{ run.failedCount }})</option>
            <option value="conflicts">CMDB conflicts only ({{ run.conflictCount }})</option>
          </select>
          <span class="text-muted">{{ jobsTotalCount }} matching job(s)</span>
        </div>
      }

      @if (jobs.length > 0) {
        <table mat-table [dataSource]="jobs" class="mat-elevation-z2 run-jobs-table">
          <ng-container matColumnDef="id">
            <th mat-header-cell *matHeaderCellDef>Job</th>
            <td mat-cell *matCellDef="let job">{{ job.id }}</td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let job">
              <span class="badge {{ getStatusClass(job.status) }}">{{ getJobStatusLabel(job.status) }}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="orionId">
            <th mat-header-cell *matHeaderCellDef>Orion ID</th>
            <td mat-cell *matCellDef="let job">
              @if (job.trackedBusinessApplicationId) {
              <a [routerLink]="['/assessment', job.trackedBusinessApplicationId]" target="_blank"
                 title="Open assessment page">
                {{ job.orionId || job.trackedBusinessApplicationId }}
              </a>
              } @else {
              {{ job.orionId || '-' }}
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="repositoryUrl">
            <th mat-header-cell *matHeaderCellDef>Repository</th>
            <td mat-cell *matCellDef="let job">
              <a [href]="job.repositoryUrl" target="_blank" rel="noopener noreferrer" [title]="job.repositoryUrl">
                {{ getRepoName(job.repositoryUrl) }}
              </a>
            </td>
          </ng-container>

          <ng-container matColumnDef="branch">
            <th mat-header-cell *matHeaderCellDef>Branch</th>
            <td mat-cell *matCellDef="let job">{{ job.branch }}</td>
          </ng-container>

          <ng-container matColumnDef="score">
            <th mat-header-cell *matHeaderCellDef>Score</th>
            <td mat-cell *matCellDef="let job">{{ job.lightweightResult?.score ?? '-' }}</td>
          </ng-container>

          <ng-container matColumnDef="completedAt">
            <th mat-header-cell *matHeaderCellDef>Completed</th>
            <td mat-cell *matCellDef="let job">{{ job.completedAt ? (job.completedAt | utcDate) : '-' }}</td>
          </ng-container>

          <ng-container matColumnDef="tokens">
            <th mat-header-cell *matHeaderCellDef>AI tokens</th>
            <td mat-cell *matCellDef="let job" class="tokens-cell"
                [title]="job.totalTokens + ' total tokens: ' + job.totalPromptTokens + ' prompt, ' + job.totalCompletionTokens + ' completion'">
              <strong>{{ formatTokenCount(job.totalTokens) }}</strong>
              <div class="token-breakdown text-muted">
                {{ formatTokenCount(job.totalPromptTokens) }} prompt<br>
                {{ formatTokenCount(job.totalCompletionTokens) }} completion
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="outcome">
            <th mat-header-cell *matHeaderCellDef>Outcome / details</th>
            <td mat-cell *matCellDef="let job" class="outcome-cell">
              <div class="outcome-label"
                   [class.text-danger]="getJobStatusLabel(job.status) === 'Failed'"
                   [class.text-warning]="isCmdbConflict(job)">
                <strong>{{ getOutcomeLabel(job) }}</strong>
              </div>
              @if (job.errorMessage) {
              <div class="outcome-detail text-danger" [title]="job.errorMessage">{{ job.errorMessage }}</div>
              } @else if (job.lightweightResult?.message) {
              <div class="outcome-detail" [title]="job.lightweightResult.message">{{ job.lightweightResult.message }}</div>
              }
              @if (job.lightweightResult?.recommendedAction) {
              <div class="outcome-detail text-muted" [title]="job.lightweightResult.recommendedAction">
                Action: {{ job.lightweightResult.recommendedAction }}
              </div>
              }
              @if (job.lightweightResult) {
              <button type="button" mat-stroked-button class="ai-json-button" (click)="viewAiJson(job)">
                <mat-icon>data_object</mat-icon>
                AI JSON
              </button>
              }
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="jobColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: jobColumns"></tr>
        </table>
        <mat-paginator
          [length]="jobsTotalCount"
          [pageIndex]="jobsPage - 1"
          [pageSize]="jobsPageSize"
          [pageSizeOptions]="[10, 25, 50, 100]"
          [showFirstLastButtons]="true"
          (page)="onJobsPageChange($event)">
        </mat-paginator>
      } @else if (jobsOutcomeFilter !== 'all') {
        <div class="alert alert-info">No jobs match the selected filter.</div>
      }
    }
  </mat-card-content>
</mat-card>
